import React, { createContext, useContext, ReactNode, useEffect, useMemo } from "react";
import { useTenantContext } from "./TenantProvider";
import { applyTheme } from "@/shared/lib/utils";
import { TenantConfig } from "@/config/schemas/tenantConfig.schema";

interface ThemeContextType {
  branding: TenantConfig['branding'] | null;
  theme: TenantConfig['theme'] | null;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  branding: null,
  theme: null,
  loading: true,
});

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { tenant, loading } = useTenantContext();
  const branding = tenant?.branding ?? null;
  const theme = tenant?.theme ?? null;

  useEffect(() => {
    if (tenant) {
      applyTheme(tenant);

      // Set document title and favicon from branding
      document.title = tenant.branding?.appName || 'Ecolane';
      const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (favicon && tenant.branding?.faviconPath) {
        favicon.href = tenant.branding.faviconPath;
      }
    }
  }, [tenant, loading]);

  const contextValue = useMemo(() => ({ branding, theme, loading }), [branding, theme, loading]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
};
